import { ContentCopy } from '@mui/icons-material'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import { useAPIKeys, type APIKeyCreateResponse } from 'hooks/useAPIKeys'
import useAuth from 'hooks/useAuth'
import React, { useState } from 'react'
import type { Organization } from 'types/organization'

interface OrganizationAPIMgmtSectionProps {
  organization: Organization
}

const OrganizationAPIMgmtSection: React.FC<OrganizationAPIMgmtSectionProps> = ({ organization }) => {
  const { token } = useAuth()
  const { createAPIKey, isLoading } = useAPIKeys(token!)

  const [apiKeyName, setApiKeyName] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [createdAPIKey, setCreatedAPIKey] = useState<APIKeyCreateResponse | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Only show for root organizations
  const isRootOrg = organization.id === organization.rootOrganizationId
  if (!isRootOrg) return null

  const handleCreateAPIKey = async () => {
    if (!apiKeyName.trim()) {
      setSnackbar({
        open: true,
        message: 'API key name is required',
        severity: 'error',
      })
      return
    }

    try {
      const response = await createAPIKey({
        organizationId: organization.id,
        name: apiKeyName.trim(),
        permissions: [''], // Empty string as specified
      })

      setCreatedAPIKey(response)
      setDialogOpen(true)
      setApiKeyName('')
      setSnackbar({
        open: true,
        message: 'API key created successfully',
        severity: 'success',
      })
    } catch (error) {
      console.error('Failed to create API key:', error)
      setSnackbar({
        open: true,
        message: 'Failed to create API key',
        severity: 'error',
      })
    }
  }

  const handleCopyAPIKey = async () => {
    if (createdAPIKey?.plainKey) {
      try {
        await navigator.clipboard.writeText(createdAPIKey.plainKey)
        setSnackbar({
          open: true,
          message: 'API key copied to clipboard',
          severity: 'success',
        })
      } catch (error) {
        console.error('Failed to copy API key:', error)
        setSnackbar({
          open: true,
          message: 'Failed to copy API key',
          severity: 'error',
        })
      }
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setCreatedAPIKey(null)
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  return (
    <>
      <Paper elevation={0} sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom mb={2}>
          API Key Management
        </Typography>
        <Grid container spacing={3} p={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              variant="outlined"
              pill
              shrink
              fullWidth
              label="API Key Name"
              value={apiKeyName}
              onChange={(e) => setApiKeyName(e.target.value)}
              placeholder="Enter a name for your API key"
              helperText="Choose a descriptive name to identify this API key"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={handleCreateAPIKey}
              disabled={isLoading || !apiKeyName.trim()}
              fullWidth
            >
              {isLoading ? 'Creating...' : 'Create API Key'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* API Key Created Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '300px' },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" component="div">
            API Key Created Successfully
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> This is the only time you will be able to see the API key. Please copy it now
              and store it securely.
            </Typography>
          </Alert>

          {createdAPIKey && (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  API Key Details
                </Typography>
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Name"
                  value={createdAPIKey.name}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  pill
                  shrink
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="API Key"
                  value={createdAPIKey.plainKey}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyAPIKey} edge="end" aria-label="copy API key" title="Copy API key">
                        <ContentCopy />
                      </IconButton>
                    ),
                  }}
                  pill
                  shrink
                  helperText="Click the copy icon to copy the API key to your clipboard"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Created At"
                  value={new Date(createdAPIKey.createdAt).toLocaleString()}
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  pill
                  shrink
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default OrganizationAPIMgmtSection
